import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/calendar").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const byDate = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmt = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="page-calendar">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">All portfolio deadlines and appointments</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" className="p-2 rounded-lg border border-border hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-heading font-semibold w-40 text-center">{MONTHS[month]} {year}</span>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" className="p-2 rounded-lg border border-border hover:bg-muted"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border">
          {DOW.map((d) => <div key={d} className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const ds = d ? fmt(d) : null;
            const evs = ds ? byDate[ds] || [] : [];
            return (
              <div key={ds || `empty-${i}`} className={`min-h-[110px] border-b border-r border-border p-1.5 ${ds === todayStr ? "bg-blue-50 dark:bg-blue-500/10" : ""}`}>
                {d && (
                  <>
                    <div className={`text-xs font-medium mb-1 ${ds === todayStr ? "text-primary font-bold" : "text-muted-foreground"}`}>{d}</div>
                    <div className="space-y-1">
                      {evs.slice(0, 3).map((e) => (
                        <div key={e.id} className="text-[10px] leading-tight px-1.5 py-1 rounded text-white truncate" style={{ backgroundColor: e.color }} title={e.title}>{e.title}</div>
                      ))}
                      {evs.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{evs.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
